'use client';
// Onglet « Indicateurs » de l'écran Maintenances (spec 002, récit 8, R12 —
// EF-041/042, 04/11/2026).
//
// Les chiffres de pilotage du plan : par mois, ce qui était dû, ce qui a été
// fait dans le délai, hors délai, ce qui reste en retard, ce qui a été clos
// « non réalisée » (avec son motif et son auteur) et ce qui était suspendu
// (appareil hors service). Rien n'est calculé ici : tout vient de
// `GET ged/maintenances/indicateurs` (`maintenance-indicateurs.ts`, côté
// serveur), le même calcul que la revue de direction.
import React, { useEffect, useState } from 'react';
import { fmtDate } from '@/lib/meta';
import { card, inputStyle, Selecteur } from '@/components/ui';
import { CelluleLien, Tableau, type ColonneTriable } from '@/shared/Tableau';
import { useGed } from '@/lib/store';
import { cheminMaintenanceAppareil } from '@/lib/maintenance-appareil';
import { lbl } from './commun';

interface Mois {
  mois: string; dues: number; faitesDansDelai: number; faitesHorsDelai: number; enRetard: number;
  sansDelai: number; dansLeDelai: number; nonRealisees: number; suspendues: number; taux: number | null;
}
interface NonRealisee { ref: string; date: string; equipementCode: string; equipementNom?: string; libelle: string; motif: string; par: string }
interface Reponse {
  du: string; au: string; poste: string; aujourdhui: string;
  parMois: Mois[]; total: Omit<Mois, 'mois'>; nonRealisees: NonRealisee[];
  joursArret: { total: number; appareils: { code: string; nom: string; jours: number }[] };
  appareilsSuivis: number;
  couverture: { parc: number; couverts: number; sansPlan: number; lecture: string };
  postes: { code: string; libelle: string }[];
  sansTolerance: string[];
}

const NOMS_MOIS = ['janvier', 'février', 'mars', 'avril', 'mai', 'juin', 'juillet', 'août', 'septembre', 'octobre', 'novembre', 'décembre'];
const direMois = (m: string) => `${NOMS_MOIS[Number(m.slice(5, 7)) - 1] || m} ${m.slice(0, 4)}`;
const direTaux = (t: number | null) => (t === null ? '—' : `${String(t).replace('.', ',')} %`);
const taux = (faites: number, dues: number) => (dues > 0 ? Math.round((faites / dues) * 1000) / 10 : null);

/** Une colonne de comptage : nombre, trié, totalisé au pied. */
const compte = (cle: keyof Omit<Mois, 'mois' | 'taux'>, libelle: string, aide: string, alerte = false): ColonneTriable<Mois> => ({
  cle, libelle, type: 'nombre', aide, export: true, valeur: (m) => m[cle],
  rendu: (m) => <span style={{ fontWeight: alerte && m[cle] > 0 ? 700 : undefined, color: alerte && m[cle] > 0 ? 'var(--red-600)' : undefined }}>{m[cle]}</span>,
});

const COLONNES: ColonneTriable<Mois>[] = [
  { cle: 'mois', libelle: 'Mois', valeur: (m) => m.mois, export: true, exporter: (m) => direMois(m.mois), rendu: (m) => direMois(m.mois) },
  compte('dues', 'Dues', 'Échéances échues du mois, hors suspendues (appareil hors service).'),
  compte('faitesDansDelai', 'Faites dans le délai', 'Déclarées faites dans le délai de tolérance — ou sans délai paramétré (retard non qualifiable).'),
  compte('faitesHorsDelai', 'Faites hors délai', 'Déclarées faites au-delà du délai de tolérance.'),
  compte('enRetard', 'En retard', 'Non faites, au-delà du délai de tolérance.', true),
  compte('sansDelai', 'Échues sans délai', 'Non faites ; aucun délai de tolérance n’est paramétré pour leur fréquence : le retard ne peut pas être qualifié.', true),
  compte('dansLeDelai', 'À faire, dans le délai', 'Non faites, encore dans le délai de tolérance (ou du jour).'),
  compte('nonRealisees', 'Non réalisées', 'Closes sans être faites, avec un motif (registre ci-dessous).'),
  compte('suspendues', 'Suspendues', 'Appareil hors service ce jour-là : ni dues, ni en retard.'),
  {
    cle: 'taux', libelle: 'Taux', type: 'nombre', export: true, aide: 'Faites dans le délai ÷ dues.',
    valeur: (m) => (m.taux === null ? -1 : m.taux), exporter: (m) => direTaux(m.taux),
    rendu: (m) => <strong>{direTaux(m.taux)}</strong>,
  },
];

const COLONNES_NR: ColonneTriable<NonRealisee>[] = [
  { cle: 'date', libelle: 'Date', type: 'date', export: true, valeur: (n) => n.date, exporter: (n) => fmtDate(n.date),
    rendu: (n) => <span style={{ fontFamily: 'var(--font-mono)' }}>{fmtDate(n.date)}</span> },
  { cle: 'appareil', libelle: 'Appareil', type: 'liste', export: true, valeur: (n) => `${n.equipementCode} ${n.equipementNom || ''}`.trim(),
    rendu: (n) => (
      <CelluleLien href={cheminMaintenanceAppareil(n.equipementCode)}>
        <span style={{ fontFamily: 'var(--font-mono)' }}>{n.equipementCode}</span> <span style={{ color: 'var(--text-muted)' }}>{n.equipementNom}</span>
      </CelluleLien>
    ) },
  { cle: 'acte', libelle: 'Acte', export: true, valeur: (n) => n.libelle, style: { whiteSpace: 'normal' },
    rendu: (n) => <>{n.libelle} <span style={{ color: 'var(--text-muted)', fontFamily: 'var(--font-mono)' }}>{n.ref}</span></> },
  { cle: 'motif', libelle: 'Motif', export: true, valeur: (n) => n.motif, style: { whiteSpace: 'normal' }, rendu: (n) => n.motif || '—' },
  { cle: 'par', libelle: 'Par', type: 'liste', export: true, valeur: (n) => n.par, rendu: (n) => n.par || '—' },
];

/** Le premier jour du mois, `n` mois avant celui d'aujourd'hui. */
function debutDeMois(auj: string, n: number): string {
  const rang = Number(auj.slice(0, 4)) * 12 + Number(auj.slice(5, 7)) - 1 - n;
  return `${Math.floor(rang / 12)}-${String((rang % 12) + 1).padStart(2, '0')}-01`;
}

export function MaintenanceIndicateurs({ aujourdhui }: { aujourdhui: string }) {
  const { session } = useGed();
  const [du, setDu] = useState(() => debutDeMois(aujourdhui, 5));
  const [au, setAu] = useState(aujourdhui);
  const [poste, setPoste] = useState('');
  const [rep, setRep] = useState<Reponse | null>(null);
  const [chargement, setChargement] = useState(true);
  const [erreur, setErreur] = useState<string | null>(null);
  const [tour, setTour] = useState(0);

  useEffect(() => {
    let vivant = true;
    const p = new URLSearchParams({ du, au });
    if (poste) p.set('poste', poste);
    void fetch(`/api/ged/ged/maintenances/indicateurs?${p.toString()}`, { credentials: 'include' })
      .then(async (r) => {
        if (!r.ok) {
          const j = await r.json().catch(() => null) as { error?: { message?: string } } | null;
          throw new Error(j?.error?.message || `Les indicateurs n’ont pas pu être lus (${r.status}).`);
        }
        return r.json() as Promise<Reponse>;
      })
      .then((d) => { if (vivant) { setRep(d); setErreur(null); } })
      .catch((e: Error) => { if (vivant) { setRep(null); setErreur(e.message); } })
      .finally(() => { if (vivant) setChargement(false); });
    return () => { vivant = false; };
  }, [du, au, poste, tour]);

  const recharger = (maj: () => void) => { setChargement(true); maj(); };
  const filtres = [`du ${fmtDate(du)} au ${fmtDate(au)}`, ...(poste ? [`poste ${rep?.postes.find((x) => x.code === poste)?.libelle || poste}`] : [])];
  const auteur = session.name || session.key;

  return (
    <div style={{ display: 'grid', gap: 12 }}>
      <div style={{ ...card, padding: '12px 14px', display: 'flex', gap: 12, flexWrap: 'wrap', alignItems: 'flex-end' }}>
        <div>
          <label style={lbl} htmlFor="mi-du">Du</label>
          <input id="mi-du" type="date" value={du} max={au} onChange={(e) => e.target.value && recharger(() => setDu(e.target.value))} style={{ ...inputStyle, width: 'auto' }} />
        </div>
        <div>
          <label style={lbl} htmlFor="mi-au">Au</label>
          <input id="mi-au" type="date" value={au} min={du} onChange={(e) => e.target.value && recharger(() => setAu(e.target.value))} style={{ ...inputStyle, width: 'auto' }} />
        </div>
        <div style={{ minWidth: 220 }}>
          <label style={lbl} htmlFor="mi-poste">Poste</label>
          <Selecteur id="mi-poste" value={poste} onChange={(v) => recharger(() => setPoste(v))}>
            <option value="">Tous les postes</option>
            {(rep?.postes || []).map((p) => <option key={p.code} value={p.code}>{p.libelle}</option>)}
          </Selecteur>
        </div>
        {rep && (
          <div style={{ display: 'flex', gap: 18, flexWrap: 'wrap', fontSize: 12.5, color: 'var(--text-muted)', marginInlineStart: 'auto' }}>
            <span title={rep.couverture.lecture}>Couverture du parc : <strong style={{ color: 'var(--text-strong)' }}>{rep.couverture.couverts}/{rep.couverture.parc}</strong> appareils avec un plan</span>
            <span title={rep.joursArret.appareils.map((a) => `${a.code} ${a.nom} : ${a.jours} j`).join(' · ') || undefined}>
              Jours d’arrêt (hors service) : <strong style={{ color: 'var(--text-strong)' }}>{rep.joursArret.total}</strong>
              {rep.joursArret.appareils.length ? ` sur ${rep.joursArret.appareils.length} appareil(s)` : ''}
            </span>
            <span>Appareils suivis : <strong style={{ color: 'var(--text-strong)' }}>{rep.appareilsSuivis}</strong></span>
          </div>
        )}
      </div>

      {!!rep?.sansTolerance.length && (
        <p style={{ margin: 0, fontSize: 12.5, color: 'var(--amber-600)', maxWidth: 820, lineHeight: 1.5 }}>
          Aucun délai de tolérance n’est paramétré pour : {rep.sansTolerance.join(', ')}. Leurs maintenances faites
          comptent « dans le délai » et les non faites restent « échues sans délai » : le retard ne peut pas être
          qualifié tant que le délai n’est pas fixé (onglet Paramétrage).
        </p>
      )}

      <div style={{ ...card, overflow: 'hidden' }}>
        <Tableau
          id="maintenance-indicateurs"
          ajusterLargeur
          defilementHorizontal
          chargement={chargement}
          erreur={erreur}
          onReessayer={() => recharger(() => setTour((t) => t + 1))}
          vide={{ titre: 'Aucun mois sur cette période.', indice: 'Choisissez une période qui commence avant aujourd’hui.' }}
          lignes={rep?.parMois || []}
          cleLigne={(m) => m.mois}
          colonnes={COLONNES}
          pied={(c, lignes) => {
            if (c.cle === 'mois') return <strong>Total</strong>;
            const somme = (k: keyof Omit<Mois, 'mois' | 'taux'>) => lignes.reduce((s, m) => s + m[k], 0);
            if (c.cle === 'taux') return <strong>{direTaux(taux(somme('faitesDansDelai'), somme('dues')))}</strong>;
            return <strong>{somme(c.cle as keyof Omit<Mois, 'mois' | 'taux'>)}</strong>;
          }}
          export={{ registre: 'maintenance-indicateurs', libelleRegistre: 'Indicateurs du plan de maintenance', auteur, filtres }}
        />
      </div>

      <div style={{ display: 'grid', gap: 6 }}>
        <h3 style={{ margin: 0, fontSize: 12.5, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.05em', color: 'var(--text-strong)' }}>
          Registre des échéances non réalisées
        </h3>
        <div style={{ ...card, overflow: 'hidden' }}>
          <Tableau
            id="maintenance-non-realisees"
            recherche
            ajusterLargeur
            chargement={chargement}
            erreur={erreur}
            onReessayer={() => recharger(() => setTour((t) => t + 1))}
            vide={{
              titre: 'Aucune échéance close « non réalisée » sur cette période.',
              indice: 'Une échéance se clôt « non réalisée », avec son motif, depuis la fenêtre « Déclarer faite ».',
            }}
            lignes={rep?.nonRealisees || []}
            cleLigne={(n) => `${n.ref}|${n.date}`}
            colonnes={COLONNES_NR}
            export={{ registre: 'maintenance-non-realisees', libelleRegistre: 'Échéances de maintenance non réalisées', auteur, filtres }}
          />
        </div>
      </div>
    </div>
  );
}
